import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { getBills, deleteBill } from '../../../../services/finance_services/billService';
import { exportBillsListExcel } from '../../../../services/export_services/billExportService';
import useApi from '../../../../hooks/useApi';
import ConfirmationModal from '../../../../components/ConfirmationModal';
import { Plus, Filter, Search, ChevronDown, CalendarRange, Download, Eye, Edit, Trash2 } from 'lucide-react';
import Message from '../../../../components/Message';
import ReportTable from '../../../../components/ReportTable';
import { formatDate } from '../../../../utils/date';
import Pagination from '../../../../components/Pagination';

function BillsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    dateRange: '',
    startDate: '',
    endDate: '',
  });

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    count: 0
  });
  const [statuses, setStatuses] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Use the useApi hook
  const { 
    data: billsData, 
    loading, 
    error, 
    callApi: fetchBills 
  } = useApi(getBills, {showSuccessToast: false});

  const { 
    loading: deleteLoading, 
    error: deleteError, 
    callApi: callDeleteBill 
  } = useApi(deleteBill, {showSuccessToast: true, msg:'Bill deleted successfully'});

  useEffect(() => {
    loadBills();
  }, [pagination.page, pagination.page_size]);

  const loadBills = async () => {
    const apiFilters = {
      search: searchTerm,
      status: filters.status,
      date_range: filters.dateRange,
      start_date: filters.startDate,
      end_date: filters.endDate,
    };
    
    const data = await fetchBills(pagination.page, pagination.page_size, apiFilters);
    
    if (data) {
      // Extract unique statuses
      const uniqueStatuses = [...new Set(data.results?.map(bill => bill.status_display))].filter(Boolean);
      setStatuses(uniqueStatuses);
      
      setPagination(prev => ({
        ...prev,
        total_pages: data.total_pages || 0,
        count: data.count || 0
      }));
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, page_size: newPageSize, page: 1 }));
  };

  const handleDeleteClick = (bill) => {
    setBillToDelete(bill);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!billToDelete) return;
    
    await callDeleteBill(billToDelete.id);
    
    if (!deleteError) {
      // Reload bills after successful deletion
      await loadBills();
      setShowDeleteModal(false);
      setBillToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setBillToDelete(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadBills();
  };

  const resetFilters = () => {
    setFilters({
      status: '',
      dateRange: '',
      startDate: '',
      endDate: '',
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    // Reload with cleared filters
    setTimeout(() => loadBills(), 0);
  };

  const handleExportBills = async () => {
    setExportError(null);
    setExporting(true);
    try {
      const exportFilters = {
        start_date: filters.startDate,
        end_date: filters.endDate,
      };
      
      const result = await exportBillsListExcel(exportFilters);
      if (result.success) {
        // Success message is handled by the download
      } else {
        setExportError(result.error || 'Failed to export bills to Excel.');
      }
    } catch (err) {
      setExportError(err.message || 'Failed to export bills to Excel.');
    } finally {
      setExporting(false);
    }
  };

  const bills = billsData?.results || [];

  const columns = [
    { label: 'Title', key: 'title' },
    { label: 'Vendor', key: 'vendor' },
    { label: 'Bill Number', key: 'bill_number' },
    { label: 'Amount', key: 'amount' },
    { label: 'Status', key: 'status_display' },
    { label: 'Due Date', key: 'due_date', format: formatDate },
    {
      key: 'actions',
      render: (row) => (
        <div className="flex gap-2">
          <button
            className="text-primary-600 hover:bg-primary-100 p-1 rounded"
            onClick={() => navigate(`/dashboard/bills/${row.id}`)}
            title="View"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            className="text-tertiary-600 hover:bg-tertiary-100 p-1 rounded"
            onClick={() => navigate(`/dashboard/bills/${row.id}/edit`)}
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            className="text-red-600 hover:bg-red-100 p-1 rounded"
            onClick={() => handleDeleteClick(row)}
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bills</h1>
          <p className="text-gray-600">Manage and track your Bills</p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
          <button 
            onClick={() => navigate('/dashboard/bills/new')} 
            className="inline-flex items-center px-4 py-2 bg-primary-600 rounded-lg text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Bill
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input 
              type="text" 
              placeholder="Search bills..." 
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  applyFilters();
                }
              }}
            />
          </div>
          {/* Filter, Reset, Export */}
          <div className="flex gap-2">
            <button 
              onClick={applyFilters}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700"
            >
              Apply Filters
            </button>
            <button 
              onClick={() => setShowFilters(!showFilters)} 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2 text-gray-500" />
              <span>Filters</span>
              <ChevronDown className={`h-4 w-4 ml-2 text-gray-500 transition-transform ${showFilters ? 'transform rotate-180' : ''}`} />
            </button>
            <button 
              onClick={resetFilters} 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              Reset
            </button>
            <button 
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm flex items-center text-gray-700 hover:bg-gray-50"
              onClick={handleExportBills}
              disabled={loading || exporting}
            >
              <Download className="h-4 w-4 mr-2 text-gray-500" />
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </button>
          </div>
        </div>
        {/* Extended Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status filter */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                id="status"
                name="status"
                className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            
            {/* Date Range filter */}
            <div>
              <label htmlFor="dateRange" className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CalendarRange className="h-4 w-4 text-gray-400" />
                </div>
                <select
                  id="dateRange"
                  name="dateRange"
                  className="w-full pl-10 border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  value={filters.dateRange}
                  onChange={handleFilterChange}
                >
                  <option value="">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>
            </div>
            
            {/* Custom date range inputs */}
            {filters.dateRange === 'custom' && (
              <>
                <div>
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div>
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    name="endDate"
                    className="w-full border border-gray-300 rounded-lg text-sm p-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Error Messages */}
      <Message type="error" message={error || deleteError} />
      <Message type="error" message={exportError} />
      
      {/* Bills Table */}
      <ReportTable 
        columns={columns} 
        data={bills} 
        loading={loading || deleteLoading}
        emptyMessage="No bills found. Try adjusting your search or filter criteria."
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={pagination.total_pages}
        totalItems={pagination.count}
        pageSize={pagination.page_size}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        itemName="Bills"
      />
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Bill"
        message={billToDelete ? `Are you sure you want to delete the bill "${billToDelete.title}"? This action cannot be undone.` : ''}
      />
    </div>
  );
}

export default BillsPage;